import React, { useEffect, useRef, useState } from 'react';
import { ArrowRight, Search } from 'lucide-react';
import type { StoreSuggestionsDto } from '@xshop/shared';
import type { StorefrontProduct } from '../api/client';
import { CheckoutModal } from '../components/CheckoutModal';
import type { StoreRead } from '../elements/render/useStoreRead';
import { useCommerce, type Commerce } from './ShopCommerce';
import { cartMoney } from './cartMoney';
import { usePaused, usePopularProducts, useSearchSuggestions } from './useSearchSuggestions';
import './searchPanel.css';
import { shopWords, useShopWords } from './shopWords';
import { responsive, THUMB_SIZES } from '../elements/render/responsiveImage';
import { useShopNames } from './shopNames';

// Sleek's search panel: a rounded field at the top of the side panel, and under it, as the shopper types,
// the collections and pages whose names match, the products with their pictures and prices, and the way
// on to every result. Before anything is typed it offers the shop's newest products. Enter submits the
// words to the search page, so the panel never stands between a shopper and their results.

/** One product row, whichever read it came from. */
type ProductRow = { key: string; title: string; href: string; image: string | null; price: number; compareAt: number | null };

// Reads a suggested product into the row the panel draws.
const suggestedRow = (row: StoreSuggestionsDto['products'][number]): ProductRow => ({
  key: row.id, title: row.title, href: `/products/${encodeURIComponent(row.slug)}`, image: row.image,
  price: row.price, compareAt: row.compareAtPrice
});

// Reads one of the shop's own products into the row the panel draws.
const productRow = (row: StorefrontProduct): ProductRow => ({
  key: row.id, title: row.title, href: `/products/${encodeURIComponent(row.slug)}`, image: row.images[0]?.src ?? null,
  price: Number(row.variants[0]?.price ?? 0), compareAt: row.variants[0]?.compareAtPrice ? Number(row.variants[0].compareAtPrice) : null
});

// Draws a list of product rows under a heading: picture, name, and price with a sale struck beside it.
const ProductRows: React.FC<{ title: string; rows: readonly ProductRow[]; commerce: Commerce }> = ({ title, rows, commerce }) => (
  <section className="xe-find-section">
    <h3>{title}</h3>
    <ul className="xe-find-products">
      {rows.map((row) => (
        <li key={row.key}>
          <a className="xe-find-row" href={row.href}>
            <span className="xe-find-thumb">{row.image ? <img {...responsive(row.image, THUMB_SIZES)} alt="" width={100} height={100} loading="lazy" /> : null}</span>
            <span className="xe-find-name" dir="auto">{row.title}</span>
            <span className="xe-find-price" data-sale={row.compareAt !== null && row.compareAt > row.price ? '' : undefined}>
              {cartMoney(row.price, commerce.currency, commerce.direction)}
            </span>
          </a>
        </li>
      ))}
    </ul>
  </section>
);

// Stands in for the rows while they are read, shaped like them so nothing jumps when they land.
const RowsSkeleton: React.FC = () => {
  const names = useShopNames();
  return (
    <ul className="xe-find-products" aria-busy="true" aria-label={names.searching}>
      {[0, 1, 2].map((at) => <li key={at} className="xe-find-row"><span className="xe-find-thumb" /><span className="xe-find-skel" /></li>)}
    </ul>
  );
};

// Draws the collections and pages whose names match, as plain links under "Suggestions".
const LinkSuggestions: React.FC<{ found: StoreSuggestionsDto }> = ({ found }) => {
  const links = [
    ...found.collections.map((row) => ({ key: `c-${row.slug}`, title: row.title, href: `/collections/${encodeURIComponent(row.slug)}` })),
    ...found.pages.map((row) => ({ key: `p-${row.slug}`, title: row.title, href: `/pages/${encodeURIComponent(row.slug)}` }))
  ];
  if (links.length === 0) return null;
  return (
    <section className="xe-find-section">
      <h3>{useShopWords().suggestions}</h3>
      <ul className="xe-find-links">{links.map((link) => <li key={link.key}><a href={link.href} dir="auto">{link.title}</a></li>)}</ul>
    </section>
  );
};

// Draws what matches the words: the skeleton, why there is nothing, or the suggestions and the way to all results.
const Matches: React.FC<{ words: string; read: StoreRead<StoreSuggestionsDto>; commerce: Commerce }> = ({ words, read, commerce }) => {
  const all = <a className="xe-find-all" href={`/search?q=${encodeURIComponent(words)}`}>See all results for “{words}”<ArrowRight size={16} aria-hidden="true" /></a>;
  if (read.status === 'failed') return <><p className="xe-find-note" role="alert">Suggestions could not be loaded. Press Enter to see every result.</p>{all}</>;
  if (read.status !== 'ready' || !read.data) return <RowsSkeleton />;
  const found = read.data;
  if (found.products.length + found.collections.length + found.pages.length === 0) {
    return <p className="xe-find-note" role="status">No results for “{words}”. Check the spelling or try another word.</p>;
  }
  return (
    <>
      <LinkSuggestions found={found} />
      {found.products.length > 0 ? <ProductRows title={shopWords(commerce.language).products} rows={found.products.map(suggestedRow)} commerce={commerce} /> : null}
      {all}
    </>
  );
};

// Draws the shop's newest products before anything is typed, their skeleton while they are read, or nothing.
const Popular: React.FC<{ read: StoreRead<StorefrontProduct[]>; commerce: Commerce }> = ({ read, commerce }) => {
  if (read.status === 'loading') return <RowsSkeleton />;
  return read.data && read.data.length > 0 ? <ProductRows title={shopWords(commerce.language).popularProducts} rows={read.data.map(productRow)} commerce={commerce} /> : null;
};

// Draws the field and, under it, the newest products before anything is typed or the matches once it is.
const SearchBody: React.FC<{ commerce: Commerce }> = ({ commerce }) => {
  const [typed, setTyped] = useState('');
  const field = useRef<HTMLInputElement>(null);
  const words = usePaused(typed.trim());
  // With suggestions switched off in the theme, the panel is a plain field that submits to the search page.
  const matches = useSearchSuggestions(commerce.predictive ? words : '');
  const popular = usePopularProducts(commerce.predictive && words === '');
  // The panel takes focus as it opens; the field takes it straight after, so the shopper can just type.
  useEffect(() => { const timer = setTimeout(() => field.current?.focus(), 0); return () => clearTimeout(timer); }, []);
  return (
    <div className="xe-find">
      <form role="search" action="/search" method="get" className="xe-find-form">
        <Search size={20} aria-hidden="true" />
        <input ref={field} type="search" name="q" value={typed} onChange={(event) => setTyped(event.target.value)}
          placeholder={shopWords(commerce.language).searchProducts} aria-label={shopWords(commerce.language).searchStore} autoComplete="off" maxLength={100} />
        {typed ? <button type="button" onClick={() => { setTyped(''); field.current?.focus(); }}>{shopWords(commerce.language).clear}</button> : null}
      </form>
      {commerce.predictive && (words === '' ? <Popular read={popular} commerce={commerce} /> : <Matches words={words} read={matches} commerce={commerce} />)}
    </div>
  );
};

// Mounts the search panel for this visit; the header's search glyph opens it.
export const SearchPanel: React.FC = () => {
  const commerce = useCommerce();
  if (!commerce) return null;
  return (
    <CheckoutModal open={commerce.searching} placement="drawer" title={shopWords(commerce.language).search} closeLabel={shopWords(commerce.language).closeSearch}
      onClose={() => commerce.search(false)} heading={<h2 className="xe-cart-heading">{shopWords(commerce.language).search}</h2>}>
      {commerce.searching ? <SearchBody commerce={commerce} /> : null}
    </CheckoutModal>
  );
};
